import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Heart,
  Target,
  Users,
  Sparkles,
  HandHeart,
  GraduationCap,
  ChevronDown,
  ChevronUp,
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  MessageCircle,
  CreditCard,
  Building,
  Hash
} from "lucide-react";
import backgroundImage from "../assets/background-image.jpg";


const ExpandableSection = ({ title, children, icon }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-6 flex items-center justify-between text-left hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          {icon}
          <h3 className="text-xl font-semibold">{title}</h3>
        </div>
        {isOpen ? <ChevronUp /> : <ChevronDown />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="p-6 pt-0 border-t">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Foundation = () => {
  const programs = [
    {
      title: "OWEF Program",
      icon: <GraduationCap className="w-6 h-6 text-yellow-600" />,
      details: [
        "Documentaries to celebrate exceptional achievements in womanhood",
        "Where are the boys program",
        "Podcast/social media lives and webinars to discuss and expose unfavorable circumstances involving women and the girl child",
        "Educational/enlightenment programs on gender based related issues",
        "Men/Women debates on gender based related issues",
        "Women in trade and providing support",
        "Documentaries on traditions and practices as its affects women positively or negatively."
      ]
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-yellow-50">
      {/* Hero Section - Kept from previous version */}
      <motion.section
        className="relative h-[85vh] flex items-center justify-center text-center px-4 pt-40 md:pt-48"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center z-0"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundColor: 'rgba(0,0,0,0.5)',
            backgroundBlend: 'overlay'
          }}
        />
        <div className="relative bg-black py-16 px-6 bg-opacity-60 mx-auto text-white">
          <motion.h1
            className="text-2xl sm:text-3xl md:text-6xl font-bold mb-4"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Obele Women Empowerment Foundation (OWEF)
          </motion.h1>
          <motion.p
            className="text-lg sm:text-xl md:text-4xl mb-6"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Empowering and Celebrating Women.
          </motion.p>
          <motion.a
            href="#contact"
            className="bg-yellow-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-yellow-700 transition-colors inline-block"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Get Involved
          </motion.a>
        </div>
      </motion.section>

      {/* Mission and Vision */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">

            {/* Vision */}
            <motion.div
              className="bg-white rounded-2xl shadow-xl p-8 relative overflow-hidden"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="absolute top-0 right-0 w-32 h-32  opacity-50" />
              <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <Sparkles className="w-8 h-8 text-amber-600" />
                Our Vision
              </h3>
              <p className="text-lg text-justify text-gray-600 relative z-10">
                OWEF has been positioned to become a leading Foundation in the areas of empowerment and celebration of the successes of women in Nigeria, across Africa and in the globe.  It is expected to enjoy local and international partnersships and collabrotaions in positioning wemen and girls for their safety, financial freedow, education, awares of fundamental human rights, equal treatment and existentiality.          </p>
            </motion.div>
            {/* Mission */}
            <motion.div
              className="bg-white rounded-2xl shadow-xl p-8 relative overflow-hidden"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="absolute top-0 right-0 w-32 h-32  opacity-50" />
              <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <Target className="w-8 h-8 text-amber-600" />
                Our Mission
              </h3>
              <p className="text-lg text-justify text-gray-600 relative z-10">
                OWEF intends to use social media, podcast channels, documentaries, seminars, conferences, webinars, workshops to educate, enlighten, empower, support and sheer women and young girls to reaching their full potentials. It also intends to utilize statistical records, past events and present trends to chart the course of women and girl children in closing the gender based related gaps.           </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-gradient-to-b from-yellow-50 to-white">
        <div className="container mx-auto max-w-6xl">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Aims and Objectives
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                text: "To celebrate women who are exceptional in their endeavours in Nigeria and beyond",
                icon: <Sparkles className="w-5 h-5 text-amber-600" />
              },
              {
                text: "To promote and protect the rights of women and the girl child from ill treatment within and across Africa",
                icon: <HandHeart className="w-5 h-5 text-amber-600" />
              },
              {
                text: "To empower and charge women to reach their full potentials in life",
                icon: <Target className="w-5 h-5 text-amber-600" />
              },
              {
                text: "To provide an avenue for women to network, collaborate and interact for global impact",
                icon: <Users className="w-5 h-5 text-amber-600" />
              },
              {
                text: "To charge women to be the change makers and help provide the enabling environment they seek to have",
                icon: <Sparkles className="w-5 h-5 text-amber-600" />
              },
              {
                text: "To educate, nurture the potentials of women",
                icon: <GraduationCap className="w-5 h-5 text-amber-600" />
              },
              {
                text: "To provide a community for women to share their successes and challegaes as a way to chat the course for more progress",
                icon: <MessageCircle className="w-5 h-5 text-amber-600" />
              },
              {
                text: "To provide a platform that will explain the laws and policies within africa, specifically nigeria as it affects women's contributions and participation in the scheme of things",
                icon: <Target className="w-5 h-5 text-amber-600" />
              },
              {
                text: "To annually celebrate and award indigenous high performing women as a way to motivate and set the tone and examples for other women to emulate",
                icon: <Heart className="w-5 h-5 text-amber-600" />
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-start gap-4">
                  <div className="bg-yellow-100 p-3 rounded-full flex-shrink-0">
                    {item.icon}
                  </div>
                  <p className="text-gray-700">{item.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Section - Enhanced with more details */}
      <section className="py-12 bg-yellow-600 text-white">
        <div className="container mx-auto px-4">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Our Impact
          </motion.h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              { number: "50+", label: "Women Empowered", sublabel: "Through various programs and initiatives" },
              { number: "5", label: "Training Programs", sublabel: "Conducted across Nigeria" },
              { number: "5", label: "Businesses Started", sublabel: "By program participants" },
              { number: "100+", label: "Lives Impacted", sublabel: "Including families and communities" }
            ].map((stat, index) => (
              <motion.div
                key={index}
                className="text-center bg-yellow-600 rounded-lg p-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
              >
                <div className="text-3xl font-bold mb-1">{stat.number}</div>
                <div className="text-lg font-semibold mb-1">{stat.label}</div>
                <div className="text-sm opacity-80">{stat.sublabel}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Section - With Expandable Details */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Our Programs
          </motion.h2>

          <div className="space-y-6">
            {programs.map((program, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
              >
                <ExpandableSection title={program.title} icon={program.icon}>
                  <ul className="space-y-4">
                    {program.details.map((detail, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Target className="w-5 h-5 text-yellow-600 mt-1" />
                        <span className="text-gray-700 text-justify">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </ExpandableSection>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Get Involved Section - With Complete Details */}
      <section id="contact" className="pb-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Get Involved
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Volunteer",
                description: "Join our community of volunteers and make a direct impact.",
                points: [
                  "Teaching and training",
                  "Mentorship",
                  "Event organization",
                  "Digital marketing",
                  "Program coordination"
                ],
                icon: <HandHeart className="w-8 h-8" />,
                color: "yellow"
              },
              {
                title: "Donate",
                description: "Support our initiatives through various contributions.",
                points: [
                  "Financial contributions",
                  "Equipment and resources",
                  "Facility space",
                  "Educational materials",
                  "Professional services"
                ],
                icon: <Heart className="w-8 h-8" />,
                color: "purple"
              },
              {
                title: "Partner",
                description: "Create lasting impact through meaningful partnerships.",
                points: [
                  "Corporate partner",
                  "NGO/foundation",
                  "Educational institution",
                  "Government agency",
                  "Individual sponsor"
                ],
                icon: <Users className="w-8 h-8" />,
                color: "teal"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="group relative bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
              >
                <div className={`absolute top-0 left-0 w-full h-2 bg-${item.color}-600`} />
                <div className="p-8">
                  <div className={`text-${item.color}-600 mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    {item.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
                  <p className="text-gray-600 mb-6">{item.description}</p>
                  <ul className="space-y-3">
                    {item.points.map((point, i) => (
                      <li key={i} className="flex items-center gap-3 text-gray-600">
                        <ArrowRight className={`w-4 h-4 text-${item.color}-600`} />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* <div className={`p-4 bg-gradient-to-r from-${item.color}-50 to-white border-t`}>
              <button className={`text-${item.color}-600 font-semibold flex items-center gap-2 group-hover:gap-3 transition-all duration-300`}>
                Learn More
                <ArrowRight className="w-4 h-4" />
              </button>
            </div> */}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="pt-8 bg-gradient-to-b from-white to-yellow-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Contact Us
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div>
                <h3 className="text-2xl font-bold mb-6">Get in Touch</h3>
                {/* <p className="text-gray-600 mb-8">
                  Have questions about our programs or want to get involved? We'd love to hear from you.
                  Reach out to us through any of these channels.
                </p> */}
              </div>

              <div className="space-y-6">
                <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-yellow-100 p-3 rounded-full">
                    <Mail className="text-yellow-600 w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-semibold">Email Us</div>
                    <div className="text-gray-600">foundationowef@gmail.com</div>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-purple-100 p-3 rounded-full">
                    <Phone className="text-purple-600 w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-semibold">Call Us</div>
                    <div className="text-gray-600">+234 123 456 7890</div>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-teal-100 p-3 rounded-full">
                    <MapPin className="text-teal-600 w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-semibold">Visit Us</div>
                    <div className="text-gray-600">Abuja, Nigeria</div>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-green-100 p-3 rounded-full">
                    <MessageCircle className="text-green-600 w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-semibold">WhatsApp</div>
                    <div className="text-gray-600">+234 916 632 4931</div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="bg-white rounded-2xl shadow-xl p-8"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold mb-6">Support Our Cause</h3>
              <div className="space-y-6">
                <p className="text-gray-600">
                  Your donation can make a real difference in the lives of those we serve.
                  Every contribution, no matter how small, helps us continue our important work.
                </p>

                <div className="bg-yellow-50 p-6 rounded-xl border border-yellow-200">
                  <h4 className="text-xl font-semibold mb-3">Donation Account Details</h4>

                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <CreditCard className="text-yellow-600 w-5 h-5 mt-1" />
                      <div>
                        <div className="font-medium">Account Name</div>
                        <div className="text-gray-700">Obele Women Empowerment Foundation</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Building className="text-yellow-600 w-5 h-5 mt-1" />
                      <div>
                        <div className="font-medium">Bank Name</div>
                        <div className="text-gray-700">Globus Bank</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Hash className="text-yellow-600 w-5 h-5 mt-1" />
                      <div>
                        <div className="font-medium">Account Number</div>
                        <div className="text-gray-700 text-lg font-semibold">1000406016</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Foundation;